/**
 * Video walkthrough embed normalization (T-063, PR-20, TECH-15).
 *
 * Pro deliverables store a `video_url` and `video_provider` on the deliverable
 * row (DATA-06, TECH-15). Operators paste the natural share URL (a Loom share
 * link, a YouTube watch link, a Vimeo link); this helper converts that to the
 * provider's embeddable URL so the <iframe> in DeliverableLayout renders the
 * player. ARCH-005 §4: there is no Pro-specific renderer; video is a
 * layout-level concern, and the embed (gated on isPro && videoUrl) is a media
 * player, NOT a deliverable-page embed, so it is compliant with the ARCH-005
 * "no iframe" Don't per the 2026-05-15 amendment.
 *
 * If the provider is unknown or the URL does not match an expected share
 * shape, we fall back to the raw URL (Loom/YouTube/Vimeo embed paths already
 * pass through unchanged; a self-hosted mp4 link would also pass through and
 * the layout will simply iframe it). Returning null is reserved for an empty
 * url so the caller can suppress the block entirely.
 */

import type { VideoProvider } from '@studio/api-schemas';

export function toEmbedUrl(
  rawUrl: string | null | undefined,
  provider: VideoProvider | string | null | undefined,
): string | null {
  if (!rawUrl || rawUrl.trim().length === 0) return null;
  const url = rawUrl.trim();

  // Loom: https://www.loom.com/share/<id> -> /embed/<id>
  if (provider === 'loom' || /(^|\.)loom\.com\//i.test(url)) {
    const m = /loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/i.exec(url);
    if (m) return `https://www.loom.com/embed/${m[1]}`;
    return url;
  }

  // YouTube: watch?v=<id> | youtu.be/<id> | /embed/<id> -> /embed/<id>
  if (
    provider === 'youtube' ||
    /(^|\.)youtube\.com\//i.test(url) ||
    /(^|\.)youtu\.be\//i.test(url)
  ) {
    const m =
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i.exec(
        url,
      );
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    return url;
  }

  // Vimeo: vimeo.com/<id> -> player.vimeo.com/video/<id>
  if (provider === 'vimeo' || /(^|\.)vimeo\.com\//i.test(url)) {
    const m = /vimeo\.com\/(?:video\/)?(\d+)/i.exec(url);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
    return url;
  }

  return url;
}
